import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Trash2, ExternalLink, Image as ImageIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import MediaPicker from "@/components/MediaPicker";

function Text({ label, value, onChange, testid, help, type = "text" }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? (parseInt(e.target.value, 10) || 0) : e.target.value)} data-testid={testid} className="input-line mt-3" />
      {help && <p className="mt-2 text-[12px] text-ink/50 italic">{help}</p>}
    </div>
  );
}
function Area({ label, value, onChange, testid, rows = 3 }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={rows} className="input-line mt-3 resize-y" />
    </div>
  );
}

function ImagePick({ label, value, onChange, testid, help }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <div className="mt-3 flex items-start gap-4">
        <div className="w-32 h-32 bg-mist/40 border border-mist flex items-center justify-center overflow-hidden flex-shrink-0">
          {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={22} className="text-ink/30" />}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-3">
            <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-pick`} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper">Choose from library</button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="text-[12px] tracking-[0.14em] uppercase text-ink/50 hover:text-copper inline-flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            )}
          </div>
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste an image URL or pick from library" data-testid={testid} className="input-line text-[13px]" />
          {help && <p className="text-[12px] text-ink/50 italic">{help}</p>}
        </div>
      </div>
      <MediaPicker open={open} onClose={() => setOpen(false)} onPick={onChange} />
    </div>
  );
}

function ListEditor({ label, items, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="eyebrow text-ink/55">{label}</label>
      <div className="mt-3 space-y-2">
        {(items || []).map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <input type="text" data-testid={`${testid}-${i}`} value={v} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }} placeholder={placeholder} className="input-line flex-1" />
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-ink/55 hover:text-copper"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...(items || []), ""])} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper" data-testid={`${testid}-add`}>+ Add</button>
      </div>
    </div>
  );
}

function ItineraryEditor({ items, onChange }) {
  const add = () => onChange([...(items || []), { day: (items?.length || 0) + 1, title: "", summary: "" }]);
  return (
    <div>
      <label className="eyebrow text-ink/55">Day-by-day itinerary</label>
      <div className="mt-3 space-y-4">
        {(items || []).map((d, i) => (
          <div key={i} className="border border-mist p-4 space-y-3">
            <div className="grid grid-cols-12 gap-3 items-center">
              <input type="number" value={d.day || ""} onChange={(e) => { const n = [...items]; n[i] = { ...d, day: parseInt(e.target.value, 10) || 0 }; onChange(n); }} placeholder="Day #" className="input-line col-span-2" data-testid={`itin-day-${i}`} />
              <input type="text" value={d.title || ""} onChange={(e) => { const n = [...items]; n[i] = { ...d, title: e.target.value }; onChange(n); }} placeholder="Day title (e.g. Arrival in Hanoi)" className="input-line col-span-9" data-testid={`itin-title-${i}`} />
              <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-ink/55 hover:text-copper col-span-1"><X size={14} /></button>
            </div>
            <textarea value={d.summary || ""} onChange={(e) => { const n = [...items]; n[i] = { ...d, summary: e.target.value }; onChange(n); }} placeholder="What happens on this day" rows={2} className="input-line resize-y" data-testid={`itin-summary-${i}`} />
          </div>
        ))}
        <button type="button" onClick={add} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper" data-testid="itin-add">+ Add day</button>
      </div>
    </div>
  );
}

const EMPTY = {
  slug: "", title: "", order: 0, status: "draft",
  duration_days: 0, from_price_gbp: 0, currency: "GBP", price_note: "per person",
  summary: "", intro: "", description: "",
  hero_image: "", card_image: "",
  destination_slugs: [], tags: [], highlights: [], itinerary: [],
  inclusions: "", best_for: "", cta_text: "Begin This Journey",
  seo: { title: "", description: "", og_title: "", og_description: "" },
};

export default function JourneyEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = slug === "new";
  const [form, setForm] = useState(isNew ? { ...EMPTY } : null);
  const [destOptions, setDestOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/destinations").then(r => setDestOptions(r.data || []));
    if (!isNew) {
      api.get(`/admin/journeys/${slug}`).then(r => setForm({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } }))
        .catch(() => { toast.error("Not found"); navigate("/studio/journeys", { replace: true }); });
    }
  }, [slug, isNew, navigate]);

  if (!form) return <p className="text-ink/50 text-sm">Loading…</p>;

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const setSeo = (k) => (v) => setForm({ ...form, seo: { ...form.seo, [k]: v } });

  const toggleDest = (s) => {
    const cur = form.destination_slugs || [];
    if (cur.includes(s)) set("destination_slugs")(cur.filter((x) => x !== s));
    else set("destination_slugs")([...cur, s]);
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!form.title?.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      let r;
      if (isNew) r = await api.post("/admin/journeys", form);
      else r = await api.put(`/admin/journeys/${slug}`, form);
      toast.success("Saved.");
      if (isNew || r.data.slug !== slug) navigate(`/studio/journeys/${r.data.slug}`, { replace: true });
      else setForm({ ...EMPTY, ...r.data, seo: { ...EMPTY.seo, ...(r.data.seo || {}) } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/journeys/${slug}`);
      toast.success("Deleted.");
      navigate("/studio/journeys", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div data-testid={`journey-edit-${slug}`}>
      <Link to="/studio/journeys" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-ink/60 hover:text-copper">
        <ChevronLeft size={14} /> Back to journeys
      </Link>
      <div className="mt-6 flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Content</p>
          <h1 className="mt-3 font-serif text-4xl text-jade font-light">{isNew ? "New journey" : form.title}</h1>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && form.status === "published" && (
            <a target="_blank" rel="noopener noreferrer" href={`/journeys/${form.slug}`} className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
              <ExternalLink size={14} /> View
            </a>
          )}
          <select data-testid="journey-status" value={form.status || "draft"} onChange={(e) => set("status")(e.target.value)} className="border border-mist px-3 py-2 text-[13px] bg-ivory">
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      <form onSubmit={save} className="mt-12 space-y-14">
        <section className="space-y-6">
          <p className="eyebrow text-copper">Basics</p>
          <div className="grid md:grid-cols-2 gap-6">
            <Text label="Journey title" value={form.title} onChange={set("title")} testid="journey-title" />
            <Text label="Slug (URL)" value={form.slug} onChange={set("slug")} testid="journey-slug" help="Leave blank to auto-generate." />
            <Text label="Display order" value={form.order} onChange={set("order")} testid="journey-order" type="number" />
            <Text label="Duration (days)" value={form.duration_days} onChange={set("duration_days")} testid="journey-duration" type="number" />
            <Text label="From price" value={form.from_price_gbp} onChange={set("from_price_gbp")} testid="journey-price" type="number" />
            <Text label="Currency" value={form.currency} onChange={set("currency")} testid="journey-currency" />
            <Text label="Price note" value={form.price_note} onChange={set("price_note")} testid="journey-price-note" help="Shown beside price, e.g. 'per person, twin share'." />
            <Text label="CTA button text" value={form.cta_text} onChange={set("cta_text")} testid="journey-cta-text" />
          </div>
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Images</p>
          <ImagePick label="Hero image" value={form.hero_image} onChange={set("hero_image")} testid="journey-hero-image" />
          <ImagePick label="Card image" value={form.card_image} onChange={set("card_image")} testid="journey-card-image" help="Falls back to hero image if blank." />
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Story</p>
          <Area label="Short summary" value={form.summary} onChange={set("summary")} testid="journey-summary" rows={2} />
          <Area label="Intro paragraph" value={form.intro} onChange={set("intro")} testid="journey-intro" rows={3} />
          <Area label="Description (shown above the itinerary)" value={form.description} onChange={set("description")} testid="journey-description" rows={4} />
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Linked destinations</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {destOptions.map((d) => {
              const on = (form.destination_slugs || []).includes(d.slug);
              return (
                <label key={d.slug} className={`flex items-center gap-2 px-3 py-2 border cursor-pointer ${on ? "border-jade bg-jade/5" : "border-mist hover:border-jade"}`}>
                  <input type="checkbox" data-testid={`journey-dest-${d.slug}`} checked={on} onChange={() => toggleDest(d.slug)} />
                  <span className="text-[13px] text-ink/85">{d.name}</span>
                </label>
              );
            })}
          </div>
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">Details</p>
          <ListEditor label="Highlights" items={form.highlights} onChange={set("highlights")} placeholder="One highlight per line" testid="journey-highlight" />
          <ListEditor label="Tags" items={form.tags} onChange={set("tags")} placeholder="Culture, Slow Travel, …" testid="journey-tag" />
          <Area label="Inclusions summary" value={form.inclusions} onChange={set("inclusions")} testid="journey-inclusions" rows={3} />
          <Text label="Best for" value={form.best_for} onChange={set("best_for")} testid="journey-best-for" help="e.g. Couples, families, slow travellers" />
        </section>

        <section>
          <ItineraryEditor items={form.itinerary} onChange={set("itinerary")} />
        </section>

        <section className="space-y-6">
          <p className="eyebrow text-copper">SEO &amp; social</p>
          <Text label="Page title" value={form.seo?.title} onChange={setSeo("title")} testid="journey-seo-title" />
          <Area label="Meta description" value={form.seo?.description} onChange={setSeo("description")} testid="journey-seo-desc" rows={2} />
          <Text label="Open Graph title" value={form.seo?.og_title} onChange={setSeo("og_title")} testid="journey-seo-og-title" />
          <Area label="Open Graph description" value={form.seo?.og_description} onChange={setSeo("og_description")} testid="journey-seo-og-desc" rows={2} />
        </section>

        <div className="pt-4 flex justify-between gap-3">
          {!isNew && (
            <button type="button" onClick={remove} data-testid="journey-delete" className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
              <Trash2 size={14} /> Delete journey
            </button>
          )}
          <div className="ml-auto flex gap-3">
            <Link to="/studio/journeys" className="btn-secondary">Cancel</Link>
            <button data-testid="journey-save" disabled={saving} type="submit" className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
